<template>
  <ModalLayout ref="verificationModal" modal-id="verificationModal"
    :loading="isSavableStateLoading" @close="closeModal()">
    <template #modal-header>
     
      <h5 v-if="selectedMatch && selectedMatch.transplant_center" class="modal-title">
        {{ `Confirm Verification for Donor: ${selectedMatch.donor.code} | Match: ${selectedMatch.code}-${selectedMatch.organ.name} | TC: ${selectedMatch.transplant_center.name}
                     | Offer Date: ${selectedMatch.offer_date}` }}
      </h5>
    </template>
    <template #modal-body>
      <form @submit.prevent="signOff" v-if="selectedMatch">
        <div class="row">
          <TextArea v-model="aocNotes"
            name="notes"
            label="Notes"
            rows="3"
            cols="100" />
        </div>
        <div class="row">
          <div class="col-sm-12">
            <b>
            Would you like to verify this checklist? 
            <br>
            This will finalize the checklist, but you can still undo it if you need to make changes later.
            </b>
          </div>
        </div>
        
        <div class="save-toolbar action-row">
          <ActionToolbar :component-state="currentState" 
            toolbar-size="col-md-6"
            :primary-disabled="isSavableStateLoading"
            success-message="Successfully saved!"
            primary-button-text="Verify"
            :secondary-enabled="false"
            :error-message="formError" />  
        </div>
      </form>
    </template>
  </ModalLayout>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue';
// @ts-ignore
import ModalLayout from "@/src/components/shared/ModalLayout.vue";
// @ts-ignore
import ActionToolbar from "@/src/components/shared/ActionToolbar.vue";
// @ts-ignore
import TextArea from "@/src/components/shared/FormComponents/TextArea.vue";
// @ts-ignore
import { useChecklistStore } from '@/src/stores/checklist';
import { Match } from "@/src/models/match";
import { useSavableState } from '@/src/composables/useSavableState';
import { useModalActions } from '@/src/composables/useModalActions';

const props = defineProps({
  data: { required: false, type: Object },
  itemId: { required: false, type: String },
  matchId: { required: true, type: String },
  checklistApplicationId: { required: true, type: String }
});

const emit = defineEmits(['close']);

const checklistStore = useChecklistStore();

const {
  formError,
  currentState,
  isSavableStateLoading,
  setSavableStateBlank,
  setSavableStateLoading,
  setSavableStateProcessing,
  setSavableStateIdle,
  setSavableStateSuccess,
  handleFormError,
} = useSavableState();

// Template refs
const verificationModal = ref<InstanceType<typeof ModalLayout> | null>(null);

// Component state
const selectedMatch = ref<any>(null);
const aocNotes = ref<string | null>(null);

const { showModal, closeAllModals } = useModalActions(verificationModal, emit);

watch(() => props.matchId, async () => {
  await getModalData();
});

onMounted(async () => {
  selectedMatch.value = null;
  await getModalData();
});

function closeModal() {
  aocNotes.value = null;
  setSavableStateBlank();
  emit('close');
}

async function signOff() {
  try {
    setSavableStateProcessing();
    const payload = {
      notes: aocNotes.value,
      checklist_application_id: props.checklistApplicationId
    };
    await checklistStore.signOffForMatch(props.matchId, payload);
    setSavableStateSuccess();
  } catch (error) {
    handleFormError(error);
  }
}

async function getModalData() {
  if (!props.matchId) return;

  try {
    setSavableStateLoading();
    const response = await checklistStore.loadMatch(props.matchId);
    selectedMatch.value = Match.buildFromAPI(response);
    const activeChecklist = selectedMatch.value.checklists.find((c: any) => c.id === props.checklistApplicationId);
    aocNotes.value = activeChecklist?.verification_notes ?? null;

    setSavableStateIdle();
  } catch (error) {
    handleFormError(error);
  }
}

defineExpose({ showModal, closeModal, closeAllModals });
</script>
